import React, { useContext, useEffect, useMemo, useState } from 'react';
import AssessmentHeader from './AssessmentHeader';
import AssessmentIntro from './AssessmentIntro';
import AssessmentStep from './AssessmentStep';
import AssessmentReview from './AssessmentReview';
import AssessmentScreen from './AssessmentScreen';
import AssessmentComplete from './AssessmentComplete';
import useStartAssessment from './hooks/useStartAssessment';
import { STATUS_FINISHED, STATUS_STARTED, STATUS_UNSTARTED, getNextStep, getSequence, apiCall, typeToSection, answersAreEqual } from './util';
import clone from 'lodash/clone';
import union from 'lodash/union';
import { i18nContext } from './context/i18n.context';
import { stepContext } from './context/step.context';
import { displayContext } from './context/display.context';
import { UserContext } from './context/user.context';
import { loggingContext } from './context/logging.context';
import { modalContext } from '@/context/modal.context';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useRouter } from 'next/router';

const AssessmentContent = ({ handleAuthenticated }) => {
  const { t } = useContext(i18nContext);
  const {
    step,
    status,
    total,
    reviewing,
    complete,
    setComplete,
    goToStep,
    sequence,
    setSequence,
    assessment,
    userAssessmentId,
    checkData,
    setCheckData,
    metadata,
    api,
    answers,
    setAnswers,
    resetAnswers,
    screenData,
    setScreenData,
    holdStep,
    setHoldStep,
    dependentQuestionList,
    setDependentQuestionList
  } = useContext(stepContext);
  const { user, agencyClient } = useContext(UserContext);
  const { css, cols, layout } = useContext(displayContext);
  const modal = useContext(modalContext);
  const { trackAnsweredQuestion } = useContext(loggingContext);
  const questions = useMemo(() => {
    if (!assessment) return {};
    const questionMap = {};
    assessment.questions.forEach((q) => (questionMap[q.id] = q));
    return questionMap;
  }, [assessment]);
  const [promptData, setPromptData] = useState({});
  const [finishedPrompts, setFinishedPrompts] = useState([]);
  const [matchesAccount, setMatchesAccount] = useState(false);
  const [triggerNext, setTriggerNext] = useState(0);
  const router = useRouter();
  const loading = Object.keys(questions).length === 0;
  const startAssessment = useStartAssessment();

  useEffect(() => {
    if (triggerNext > 0 && !holdStep) {
      setTriggerNext(0);
      handleNext();
    }
  }, [triggerNext, holdStep]);

  useEffect(() => {
    // Skip intro screen when a client is passed via URL parameter, but only if there's no description to show
    if (agencyClient && !assessment?.description) {
      if (!metadata.options) metadata.options = {};
      metadata.options.skip_intro = true;
      // For unstarted assessments, we only need the setup (markAssessmentStarted + tracking) —
      // step 0 is already the first question so we must not navigate forward.
      // For started assessments, pass resetAndBegin so fetched answers are restored and
      // the user resumes from their last answered question.
      startAssessment(status === STATUS_UNSTARTED ? () => {} : resetAndBegin);
    }
    setSequence(getSequence(assessment, metadata));
    setPromptData(metadata.prompts);
  }, [assessment, metadata, agencyClient]);

  const current = (calculatedStep) => {
    const s = sequence[calculatedStep];
    return s.type === 'question' ? clone(questions[s.value]) : s.value;
  };

  const resetAndBegin = (lastCompletedStep = false, startingAnswers = {}, lastAnswer) => {
    resetAnswers({
      preserveFetchedAnswers: status == STATUS_STARTED,
      startingAnswers
    });
    if (router?.query?.startNew == 'true') {
      router.replace({ pathname: router.pathname, query: { ...omit(router.query, 'startNew') } }, undefined, { shallow: true });
    }
    saveAndContinue(startingAnswers, lastAnswer, false, lastCompletedStep);
  };

  const handleNext = (e) => saveAndContinue(null);

  const saveAndContinue = async (answerList, lastAnswer, answerChanged, lastCompletedStep = false) => {
    let calculatedStep = lastCompletedStep !== false ? lastCompletedStep : step;
    let nextStep = calculatedStep + 1;
    let modalPrompt = false;

    const question = current(calculatedStep);
    // we need to manage the list throughout this function so clone it with the intention of setting the updated value later
    let currentDependentQuestionList = lastCompletedStep !== false ? clone(question.dependent_questions) : clone(dependentQuestionList);

    if (answerChanged && question.dependent_questions?.length) {
      answerList = await clearDependentAnswers(question, answerList);
      if (reviewing) {
        currentDependentQuestionList = union(currentDependentQuestionList, question.dependent_questions);
      }
    }
    const res = getNextStep(
      questions,
      answerList,
      calculatedStep,
      sequence,
      question,
      lastAnswer,
      reviewing,
      promptData,
      finishedPrompts,
      currentDependentQuestionList
    );
    if (res.modalPrompt) modalPrompt = res.modalPrompt;
    else if (answerList) nextStep = res.nextStep; // not setting to null at the end bc answerList is null, but maybe that's ok
    if (answerList) setAnswers(answerList);
    if (typeof lastAnswer !== 'undefined') {
      await saveAnswer(question, lastAnswer);
    }

    setDependentQuestionList(currentDependentQuestionList);

    const stepsComplete = nextStep === null || nextStep > total - 1;

    if (modalPrompt) launchModal(modalPrompt);
    else if (stepsComplete) handleComplete();
    else goToStep(nextStep, reviewing && currentDependentQuestionList.length);
  };

  const handleSelection = (answer) => {
    // null should clear the answer; undefined should skip it
    const answerList = clone(answers);
    const question_id = current(step).id;
    const answerChanged = typeof answerList[question_id] !== 'undefined' && !answersAreEqual(answerList[question_id].value, answer);
    answerList[question_id] = { value: clone(answer), question_id, step };
    saveAndContinue(answerList, answer, answerChanged);
  };

  const handleComplete = () => {
    setComplete(true);
    setCheckData((prevState) => ({ ...prevState, status: STATUS_FINISHED }));
    setFinishedPrompts([]);
    window.scrollTo(0, 0);
  };

  const handleScreen = (answer) => {
    const screens = clone(screenData);
    const page_id = current(step).id;
    screens[page_id] = { value: clone(answer), page_id, step };
    setScreenData(screens);
    handleNext();
  };

  const baseApiData = {
    userAssessmentId,
    assessmentId: assessment.id,
    agencyClientId: agencyClient?.id
  };

  const clearDependentAnswers = async (question, answerList) => {
    // loop through answerList and clear dependent questions
    if (answerList) {
      question.dependent_questions.map((qId) => {
        if (answerList[qId]) {
          delete answerList[qId];
        }
      });
    }
    if (metadata.options?.save_after_each_answer !== 'false') {
      await Promise.all(
        question.dependent_questions.map((qId) => {
          return apiCall(async () => {
            await api.deleteAssessmentAnswer(Object.assign({ questionId: qId }, baseApiData));
          });
        })
      );
    }
    return answerList;
  };

  const saveAnswer = async (question, answer) => {
    if (metadata.options?.save_after_each_answer === 'false') {
      return true;
    }
    // TODO disable interface elements while we're waiting for the response?
    // TODO queue these requests so we can proceed without success, and retry or give feedback?
    return apiCall(async () => {
      const baseData = Object.assign({ questionId: question.id }, baseApiData);
      await api.saveAssessmentAnswer(Object.assign({ answer }, baseData));
      trackAnsweredQuestion(baseData);
    });
  };

  const isModalRequired = (modalData) => {
    switch (modalData.prompt.component) {
      case 'ConfirmPhoneModal':
        return !agencyClient; // must confirm phone unless this is a supporter answering for a client
      case 'LoginModal':
        return true; // TODO this logic should check the user var, but that clashes with the modal logic somehow
      default:
        return true;
    }
  };

  const launchModal = (modalData) => {
    let modalProps = {
      name: modalData.prompt.component
    };

    const finishPrompt = () => {
      const key = modalData.key;
      const pid = modalData.prompt.id;
      const i = promptData[key].findIndex((p) => p.id === pid);
      if (i > -1) {
        setFinishedPrompts([...finishedPrompts, pid]);
        setTriggerNext(triggerNext + 1);
      }
    };

    if (!isModalRequired(modalData)) {
      return finishPrompt();
    }

    switch (modalProps.name) {
      case 'ConfirmPhoneModal':
        if (user?.id) {
          finishPrompt();
          return;
        }
        Object.assign(modalProps, {
          agencyId: checkData.agency_id,
          externalClientId: checkData.ext_id_check,
          onConfirm: (confirmedResponse, fullDigits) => {
            setMatchesAccount(!!confirmedResponse?.matches_account);
            const signupArgs = buildSignupArgs(confirmedResponse, checkData);

            // this is to flexibly pass data between prompts in cases where assessments
            // chain multiple prompts and some might data from one might be useful to the other (if it exists)
            // but it avoids making any prompt dependent on another
            // this current implementation assumes that the prompts all occur on the same page/step
            const loginModalIndex = promptData[modalData.key]
              .map((p, index) => {
                return ['LoginModal', 'SignupModal'].includes(p.component) ? index : false;
              })
              .filter((p) => p !== false);
            let i = 0;
            while (loginModalIndex.length > i) {
              promptData[modalData.key][loginModalIndex[i]].startingEmailOrPhoneInputValue = fullDigits;
              promptData[modalData.key][loginModalIndex[i]].signupArgs = signupArgs;
              i++;
            }

            finishPrompt();
          },
          startingDigits: checkData.phone_check
        });
        break;
      case 'LoginModal':
        modalProps.backdrop = 'static';
        modalProps.startingEmailOrPhoneInputValue = modalData.prompt.startingEmailOrPhoneInputValue || '';
        modalProps.signupArgs = modalData.prompt.signupArgs;
        modalProps.onDismiss = () => {
          setHoldStep(false);
        };
        modalProps.onAuthenticated = async () => {
          if (handleAuthenticated) {
            await handleAuthenticated();
          }
          finishPrompt();
        };
        if (user?.id) {
          finishPrompt();
          return;
        } else if (!matchesAccount) {
          modalProps.name = 'SignupModal';
          modalProps.reason = 'assessmentSignup';
        } else {
          modalProps.reason = 'assessmentLogin';
        }
        modalProps.onConfirm = async (...args) => {
          return handleAuthenticated ? handleAuthenticated(args) : Promise.resolve(args);
        };
        setHoldStep(true);
        break;
    }
    modal.enqueueModal(modalProps);
  };

  const buildSignupArgs = (responseData, checkData) => {
    if (responseData.multiple_clients) {
      // if there are multiple matching agency clients for this phone,
      // send in parameters to limit the connection of user & agency
      // client to this one only (ODAS issue #6427)
      return {
        signup_options: {
          limit_for_agency: {
            agency_id: checkData.agency_id,
            external_client_id: checkData.ext_id_check
          }
        }
      };
    } else {
      return null;
    }
  };

  const jsx = () => {
    if (!assessment || !sequence) {
      return <p>{t('loading assessment...')}</p>;
    }
    const seq = sequence[step];
    switch (seq.type) {
      case 'intro':
        return <AssessmentIntro handleNext={resetAndBegin} />;
      case 'review':
        return <AssessmentReview answers={answers} handleSaveAll={handleNext} />;
      case 'question':
        let q;
        if (Object.keys(questions).length > 0) {
          q = questions[seq.value];
        } else {
          const questionMap = {};
          assessment.questions.forEach((q) => (questionMap[q.id] = q));
          q = questionMap[seq.value];
        }

        const a = answers[q?.id];
        return <AssessmentStep key={q.id} handleNext={handleSelection} question={q} answer={a} />;
      case 'pre':
      case 'post':
        return <AssessmentScreen key={seq.value?.id} page={seq.value} handleNext={handleScreen} answer={screenData[seq.value?.id]?.value} />;
      default:
        throw new Error('Unsupported sequence step type: ' + seq.type);
    }
  };

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 50);
  }, [step]);

  const getLayoutCols = () => {
    let section = complete ? typeToSection('results') : typeToSection(sequence?.[step]?.type);
    return cols[section].container;
  };

  const clientName = agencyClient ? `${agencyClient.first_name} ${agencyClient.last_name}` : '';

  return (
    <Container fluid className={`${css('od-assessments')} ${css(layout + '-layout')}`}>
      <AssessmentHeader />
      <Row className={`${css('assessment-content-container')} ${css('assessment-padding-container')}`}>
        <Col {...getLayoutCols()}>
          {agencyClient && (
            <Row className={css('assessment-client')}>
              <Col>
                <p>{t('You are taking this assessment for a client', { clientName })}</p>
              </Col>
            </Row>
          )}
          {complete && <AssessmentComplete />}
          {!complete && !loading && jsx()}
        </Col>
      </Row>
    </Container>
  );
};

export default AssessmentContent;
